import { coinFieldVariations, salesFieldVariations } from './constants';

// Calculate match score between header and field
export const calculateMatchScore = (header, field) => {
  const normalizedHeader = header.toLowerCase().replace(/[^a-z0-9]/g, '');
  const headerWords = header.toLowerCase().split(/[\s\/\-_]+/);
  
  // Exact matches get highest score
  if (field.normalized === normalizedHeader || 
      field.key.toLowerCase() === normalizedHeader) {
    return 100;
  }

  // Variation exact matches get next highest score
  if (field.variations.some(v => 
    v.toLowerCase().replace(/[^a-z0-9]/g, '') === normalizedHeader
  )) {
    return 90;
  }

  // Check for word matches
  const fieldWords = field.label.toLowerCase().split(/[\s\/\-_]+/);
  const commonWords = headerWords.filter(hw => 
    fieldWords.includes(hw) || 
    field.variations.some(v => v.toLowerCase().split(/[\s\/\-_]+/).includes(hw))
  );

  if (commonWords.length > 0) {
    // Score based on percentage of words matched
    const headerScore = (commonWords.length / headerWords.length) * 80;
    const fieldScore = (commonWords.length / fieldWords.length) * 80;
    return Math.min(headerScore, fieldScore);
  }

  // Substring matches get lower scores
  if (field.normalized.includes(normalizedHeader) ||
      normalizedHeader.includes(field.normalized)) {
    return 30;
  }

  return 0;
};

// Function to attempt automatic field mapping
export const attemptAutoMapping = (headers, availableFields) => {
  const newMappings = {};
  const normalizedFields = availableFields.map(field => ({
    ...field,
    normalized: field.label.toLowerCase().replace(/[^a-z0-9]/g, ''),
    variations: coinFieldVariations[field.key] || []
  }));
  
  // Keep track of which fields have been mapped
  const mappedFields = new Set();

  // Process headers in order, calculating scores for all possible matches
  headers.forEach(header => {
    // Skip if this header is already mapped
    if (newMappings[header]) return;

    // Calculate scores for all available fields
    const scores = normalizedFields
      .filter(field => !mappedFields.has(field.key))
      .map(field => ({
        field,
        score: calculateMatchScore(header, field)
      }))
      .filter(match => match.score > 50) // Only consider good matches
      .sort((a, b) => b.score - a.score); // Sort by score descending

    // Use the best match if we have one
    if (scores.length > 0) {
      newMappings[header] = scores[0].field.key;
      mappedFields.add(scores[0].field.key);
    }
  });

  return newMappings;
};

// Helper function to check if value should be treated as empty
export const isEmpty = (val) => {
  if (val === undefined || val === null) return true;
  const str = val.toString().trim().toLowerCase();
  return str === '' || str === '-' || str === 'n/a';
};

// Helper function to parse Excel date numbers
export const parseExcelDate = (dateStr) => {
  if (/^\d+$/.test(dateStr)) {
    const days = parseInt(dateStr);
    return new Date(Date.UTC(1899, 11, 30 + days));
  }
  return new Date(dateStr).toISOString();
};

// Helper function to validate numeric fields
export const validateNumericField = (value, fieldKey, errors, rowIndex) => {
  if (isEmpty(value)) return null;
  
  const numValue = parseFloat(value);
  if (isNaN(numValue)) {
    errors.push(`Invalid ${fieldKey} format: "${value}" is not a valid number`);
    return null;
  }

  // Add field-specific validation
  switch (fieldKey) {
    case 'purchase_price':
    case 'price_guide_value':
    case 'price':
    case 'list_price':
      if (numValue < 0) {
        errors.push(`${fieldKey} cannot be negative: ${numValue}`);
        return null;
      }
      break;
    case 'composition':
      if (numValue < 0 || numValue > 100) {
        errors.push(`Composition must be between 0 and 100: ${numValue}`);
        return null;
      }
      break;
    case 'weight':
    case 'diameter':
      if (numValue <= 0) {
        errors.push(`${fieldKey} must be greater than 0: ${numValue}`);
        return null;
      }
      break;
  }
  
  return numValue;
};

// Helper function to validate date fields
export const validateDateField = (value, fieldKey, errors, rowIndex) => {
  if (isEmpty(value)) return null;

  try {
    const date = parseExcelDate(value);
    if (isNaN(date.getTime())) {
      errors.push(`Invalid ${fieldKey} format: "${value}" is not a valid date`);
      return null;
    }

    const dateStr = date.toISOString().split('T')[0];
    
    // Add field-specific validation
    const today = new Date().toISOString();
    today.setHours(0, 0, 0, 0);
    
    if (fieldKey === 'purchase_date' && date > today) {
      errors.push(`Purchase date cannot be in the future: ${dateStr}`);
      return null;
    }
    
    if (fieldKey === 'transaction_date') {
      if (date > today) {
        errors.push(`Transaction date cannot be in the future: ${dateStr}`);
        return null;
      }
    }

    return dateStr;
  } catch (err) {
    errors.push(`Invalid ${fieldKey} format: "${value}" could not be parsed as a date`);
    return null;
  }
};

// Helper function to validate population fields
export const validatePopulationField = (value, errors) => {
  if (isEmpty(value)) return null;
  
  const popStr = value.toString().replace(/,/g, '').split('/')[0];
  const popValue = parseInt(popStr, 10);
  
  if (isNaN(popValue)) {
    errors.push(`Invalid population value: "${value}" is not a valid number`);
    return null;
  }
  
  if (popValue < 0) {
    errors.push(`Population cannot be negative: ${popValue}`);
    return null;
  }
  
  return popValue;
};

// Helper function to validate mintage field
export const validateMintageField = (value, errors) => {
  if (isEmpty(value)) return null;
  
  const mintageStr = value.toString().replace(/,/g, '');
  const mintageValue = parseInt(mintageStr, 10);
  
  if (isNaN(mintageValue)) {
    errors.push(`Invalid mintage value: "${value}" is not a valid number`);
    return null;
  }
  
  if (mintageValue < 0) {
    errors.push(`Mintage cannot be negative: ${mintageValue}`);
    return null;
  }
  
  return mintageStr;
};
